import React from 'react';
import {Table, Tag, Tooltip, Typography} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {ReportFileType} from "../graphql/queries/getReportFileListingForCentral";
import dayjs from 'dayjs';
import {ReportFileTypeEnum, ReportFileStatusEnum} from "../../../../enums/ReportFileEnum";
import { useTranslation } from 'react-i18next';

interface CentralReportColumnsProps {
    reportFileData: ReportFileType[];
    onSelectChange: (selectedRowKeys: React.Key[], selectedRows: ReportFileType[]) => void;
    selectedRowKeys: React.Key[];
    loading: boolean;
}

const CentralReportFileColumns: React.FC<CentralReportColumnsProps> = ({
    reportFileData,
    onSelectChange,
    selectedRowKeys,
    loading,
}) => {
    const {t} = useTranslation();
    const getReportFileTypeTag = (type: number) => {
        switch (type) {
            case ReportFileTypeEnum.DAILY:
                return <Tag color="blue">{t('enum.reportTypeOptions.daily')}</Tag>;
            case ReportFileTypeEnum.WEEKLY:
                return <Tag color="green">{t('enum.reportTypeOptions.weekly')}</Tag>;
            case ReportFileTypeEnum.MONTHLY:
                return <Tag color="orange">{t('enum.reportTypeOptions.monthly')}</Tag>;
            case ReportFileTypeEnum.PARCEL_DOWNLOAD:
                return <Tag color="purple">{t('enum.reportTypeOptions.parcelDownload')}</Tag>;
            case ReportFileTypeEnum.OTHER_CARRIER_BAG_TASK:
                return <Tag color="cyan">{t('enum.reportTypeOptions.otherCarrierBagTask')}</Tag>;
            case ReportFileTypeEnum.PICKUP_APPOINTMENT:
                return <Tag color="cyan">{t('enum.reportTypeOptions.pickupAppointmentDownload')}</Tag>;
            case ReportFileTypeEnum.POD_REVIEW:
                return <Tag color="cyan">{t('enum.reportTypeOptions.podReviewDownload')}</Tag>;
            case ReportFileTypeEnum.ZIPCODE_EXPORT:
                return <Tag color="cyan">{t('enum.reportTypeOptions.zipcodeExport')}</Tag>;
            case ReportFileTypeEnum.DASHBOARD_EXPORT:
                return <Tag color="cyan">{t('enum.reportTypeOptions.dashboardExport')}</Tag>;
            case ReportFileTypeEnum.VOUCHER_CLEANING:
                return <Tag color="cyan">{t('enum.reportTypeOptions.voucherCleaning')}</Tag>;
            case ReportFileTypeEnum.ACCOUNT_TRANSACTION_FLOW:
                return <Tag color="blue">{t('enum.reportTypeOptions.accountTransactionFlow')}</Tag>;
            case ReportFileTypeEnum.ACCOUNT_BALANCE_SNAPSHOT:
                return <Tag color="purple">{t('enum.reportTypeOptions.accountBalanceSnapshot')}</Tag>;
            case ReportFileTypeEnum.TRANSACTION_FLOW_RECORD:
                return <Tag color="geekblue">{t('enum.reportTypeOptions.transactionFlowRecord')}</Tag>;
            case ReportFileTypeEnum.REALTIME_SETTLEMENT_ORDER_DETAIL:
                return <Tag color="gold">{t('enum.reportTypeOptions.realtimeSettlementOrderDetail')}</Tag>;
            case ReportFileTypeEnum.TMS_TASK:
                return <Tag color="blue">{t('enum.reportTypeOptions.tmsTaskDownload')}</Tag>;
            case ReportFileTypeEnum.EXCEPTION_DOWNLOAD:
                return <Tag color="volcano">{t('enum.reportTypeOptions.exceptionDownload')}</Tag>;
            case ReportFileTypeEnum.CHANNEL_ONBOARDING_DOCUMENT:
                return <Tag color="geekblue">{t('enum.reportTypeOptions.channelOnboardingDocument')}</Tag>;
            case ReportFileTypeEnum.DELIVERY_DRIVER_EXPORT:
                return <Tag color="lime">{t('enum.reportTypeOptions.deliveryDriverExport')}</Tag>;
            case ReportFileTypeEnum.ORDER_CLOSE_TOOL_DOWNLOAD:
                return <Tag color="magenta">{t('enum.reportTypeOptions.orderCloseToolDownload')}</Tag>;
            case ReportFileTypeEnum.CLIENT_HUB_ZONE_IMPORT_TASK:
                return <Tag color="geekblue">{t('enum.reportTypeOptions.clientHubZoneImportTaskDownload')}</Tag>;
            case ReportFileTypeEnum.PRICING_SCHEME_EXPORT:
                return <Tag color="purple">{t('enum.reportTypeOptions.pricingSchemeExport')}</Tag>;
            case ReportFileTypeEnum.DELIVERY_CONFIRM_FEEDBACK:
                return <Tag color="gold">{t('enum.reportTypeOptions.deliveryConfirmFeedback')}</Tag>;
            case ReportFileTypeEnum.BAG_TAG_EXPORT:
                return <Tag color="geekblue">{t('enum.reportTypeOptions.bagTagExport')}</Tag>;
            case ReportFileTypeEnum.WAREHOUSE_LOCATION_EXPORT:
                return <Tag color="purple">{t('enum.reportTypeOptions.warehouseLocationExport')}</Tag>;
            case ReportFileTypeEnum.DELIVERY_TASK_TRACKING_NUMBER_EXPORT:
                return <Tag color="gold">{t('enum.reportTypeOptions.deliveryTaskTrackingNumberExport')}</Tag>;
            case ReportFileTypeEnum.MISSING_UNIT_PENDING:
                return <Tag color="gold">{t('enum.reportTypeOptions.missingUnitPending')}</Tag>;
            case ReportFileTypeEnum.DSP_SUMMARY_EXPORT:
                return <Tag color="blue">{t('enum.reportTypeOptions.dspSummaryExport')}</Tag>;
            case ReportFileTypeEnum.QC_PENALTY_UPLOAD_RESULT:
                return <Tag color="cyan">{t('enum.reportTypeOptions.qcPenaltyUploadResult')}</Tag>;
            case ReportFileTypeEnum.PARCEL_SHIPPING_LABEL_PDF:
                return <Tag color="purple">{t('enum.reportTypeOptions.parcelShippingLabelPdf')}</Tag>;
            case ReportFileTypeEnum.PRICING_FAIL_DATA_DETAIL:
                return <Tag color="cyan">{t('enum.reportTypeOptions.pricingFailDataDetail')}</Tag>;
            case ReportFileTypeEnum.PRE_BILL_REVISION_TASK_DETAIL:
                return <Tag color="cyan">{t('enum.reportTypeOptions.preBillRevisionTaskDetail')}</Tag>;
            case ReportFileTypeEnum.BILLING_PERIOD_SUMMARY:
                return <Tag color="blue">{t('enum.reportTypeOptions.billingPeriodSummary')}</Tag>;
            case ReportFileTypeEnum.BILLING_PERIOD_RECEIVE_SUMMARY:
                return <Tag color="blue">{t('enum.reportTypeOptions.billingPeriodReceiveSummary')}</Tag>;
            case ReportFileTypeEnum.REALTIME_INCOME_DAILY_SUM:
                return <Tag color="gold">{t('enum.reportTypeOptions.realtimeIncomeDailySum')}</Tag>;
            case ReportFileTypeEnum.REALTIME_INCOME_DAILY_RECEIVE_SUM:
                return <Tag color="gold">{t('enum.reportTypeOptions.realtimeIncomeDailyReceiveSum')}</Tag>;
            case ReportFileTypeEnum.REVIEW_TASK_EXPORT:
                return <Tag color="cyan">{t('enum.reportTypeOptions.reviewTaskExport')}</Tag>;
            case ReportFileTypeEnum.UNDELIVERABLE_UPLOAD_DETAIL:
                return <Tag color="purple">{t('enum.reportTypeOptions.undeliverableUploadDetail')}</Tag>;
            default:
                return <Tag color="default">{t('enum.reportTypeOptions.other')}</Tag>;
        }
    };

    const getStatusTag = (status: number) => {
        switch (status) {
            case ReportFileStatusEnum.DOWNLOADING:
                return <Tag color="processing">{t('enum.reportFileStatusOptions.downloading')}</Tag>;
            case ReportFileStatusEnum.COMPLETED:
                return <Tag color="success">{t('enum.reportFileStatusOptions.completed')}</Tag>;
            case ReportFileStatusEnum.FAILED:
                return <Tag color="error">{t('enum.reportFileStatusOptions.failed')}</Tag>;
            default:
                return <Tag color="default">{t('enum.reportFileStatusOptions.other')}</Tag>;
        }
    };

    const formatDate = (dateString: string) => {
        return dayjs(dateString).format('YYYY-MM-DD');
    };

    const columns: ColumnsType<ReportFileType> = [
        {
            title: t('central.download.columns.reportDate'),
            dataIndex: 'reportDate',
            key: 'reportDate',
            width: 120,
            render: (dateString: string) => formatDate(dateString),
        },
        {
            title: t('central.download.columns.type'),
            dataIndex: 'type',
            key: 'type',
            width: 100,
            render: (type: number) => getReportFileTypeTag(type),
        },
        {
            title: t('central.download.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: number) => getStatusTag(status),
        },
        {
            title: t('central.download.columns.reportName'),
            dataIndex: 'name',
            key: 'name',
            width: 420,
            render: (name?: string) => (
                <Tooltip title={name || '-'}>
                    <Typography.Text ellipsis>
                        {name || '-'}
                    </Typography.Text>
                </Tooltip>
            ),
        },
        {
            title: t('central.download.filters.operator'),
            dataIndex: 'operatorName',
            key: 'operatorName',
            width: 150,
            render: (operatorName?: string) => operatorName,
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record: ReportFileType) => ({
            disabled: record.status === ReportFileStatusEnum.DOWNLOADING || record.status === ReportFileStatusEnum.FAILED, // 下载中和下载失败不允许选中
        }),
    };

    return (
        <Table
            columns={columns}
            dataSource={reportFileData}
            rowKey="id"
            loading={loading}
            rowSelection={rowSelection}
            pagination={false}
            scroll={{ x: 'max-content' }}
            className="mb-4"
        />
    );
};

export default CentralReportFileColumns;